import { normalizeIndianPhone } from '@dentexe/types';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { PatientCreateDto, PatientUpdateDto } from '@dentexe/types';
import type { PatientCreateInput, PatientUpdateInput } from '@dentexe/types';
import type { UpdatePatientInput } from '../domain/repositories/patient.repository';
import { markAsHandled } from '@/lib/sentry';
import {
  useCreatePatient,
  useUpdatePatient,
  useSetPatientMedicalConditions,
  useSetPatientAllergies,
  useOrgConditions,
  useOrgAllergies,
  useOrgDoctors,
} from '@/features/patients';
import { useCountries, useStates, useDistricts } from '@/shared/api/locations.hooks';
import { FormPhoneField, MultiSelectPopup, MultiSelectBadge, DatePicker } from '@/shared/ui';
import type { MultiSelectItem } from '@/shared/ui';
import { Card, CardContent } from '@/shared/ui/Card';
import { CountrySelect } from '@/shared/ui/CountrySelect';
import { Input } from '@/shared/ui/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { Textarea } from '@/shared/ui/Textarea';
import { Button } from '@/shared/ui/Button';
import { Label } from '@/shared/ui/Label';
import { NativeSelect } from '@/shared/ui/NativeSelect';
import { cn } from '@/shared/lib/utils';
import type { Patient, PatientContact } from '@/features/patients';

const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'] as const;

const GENDER_OPTIONS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
];

function calculateAge(dateOfBirthStr: string): number {
  const dob = new Date(dateOfBirthStr);
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) age--;
  return age;
}

const RELATIONSHIP_OPTIONS = [
  { value: 'spouse', label: 'Spouse' },
  { value: 'parent', label: 'Parent' },
  { value: 'sibling', label: 'Sibling' },
  { value: 'child', label: 'Child' },
  { value: 'grandparent', label: 'Grandparent' },
  { value: 'in_law', label: 'In-law' },
  { value: 'guardian', label: 'Guardian' },
  { value: 'friend', label: 'Friend' },
  { value: 'neighbour', label: 'Neighbour' },
  { value: 'other', label: 'Other' },
];

function buildContactPayload(values: Record<string, string | undefined>, effectiveCountryId: string) {
  return {
    primaryPhone: values['primaryPhone'] ? normalizeIndianPhone(values['primaryPhone']) : '',
    alternatePhone: values['alternatePhone'] ? normalizeIndianPhone(values['alternatePhone']) : undefined,
    email: values['email']?.trim() || undefined,
    addressLine1: values['addressLine1']?.trim() || undefined,
    addressLine2: values['addressLine2']?.trim() || undefined,
    city: values['city']?.trim() || undefined,
    state: values['state']?.trim() || undefined,
    nationality: values['nationality'] || 'IN',
    countryId: values['countryId'] || effectiveCountryId || undefined,
    stateId: values['stateId']?.trim() || undefined,
    districtId: values['districtId']?.trim() || undefined,
    pinCode: values['pinCode']?.trim() || undefined,
    emergencyContactName: values['emergencyContactName']?.trim() || undefined,
    emergencyContactRelation: values['emergencyContactRelation'] || undefined,
    emergencyContactPhone: values['emergencyPhone']
      ? normalizeIndianPhone(values['emergencyPhone'])
      : undefined,
  };
}

export interface PatientFormProps {
  mode: 'create' | 'edit';
  patient?: Patient;
  contact?: PatientContact | null;
  clinicDefaults?: {
    countryId?: string;
    stateId?: string;
    districtId?: string;
  };
  /** One UUID per create attempt; reused across double-clicks until success. */
  idempotencyKey?: string;
  onSuccess: (patientId: string) => void;
  onCancel?: () => void;
  onBusyChange?: (busy: boolean) => void;
}

export interface PatientFormHandle {
  submit: () => Promise<void>;
  isBusy: () => boolean;
}

export const PatientForm = forwardRef<PatientFormHandle, PatientFormProps>(
  function PatientForm(
    {
      mode,
      patient,
      contact,
      clinicDefaults,
      idempotencyKey,
      onSuccess,
      onCancel,
      onBusyChange,
    },
    ref,
  ) {
  const create = useCreatePatient();
  const update = useUpdatePatient();
  const setConditions = useSetPatientMedicalConditions();
  const setAllergies = useSetPatientAllergies();
  const { data: orgConditions } = useOrgConditions();
  const { data: orgAllergies } = useOrgAllergies();
  const { data: doctors } = useOrgDoctors();
  const { data: countries = [] } = useCountries();
  const submittingRef = useRef(false);
  const [busy, setBusy] = useState(false);

  function setSubmitting(next: boolean) {
    submittingRef.current = next;
    setBusy(next);
    onBusyChange?.(next);
  }

  const schema = mode === 'create' ? PatientCreateDto : PatientUpdateDto;
  const form = useForm({
    resolver: zodResolver(schema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      firstName: patient?.firstName ?? '',
      lastName: patient?.lastName ?? '',
              dateOfBirth: patient?.dateOfBirth instanceof Date && !isNaN(patient.dateOfBirth.getTime())
        ? patient.dateOfBirth.toISOString().split('T')[0]
        : undefined,
              age: patient?.age ?? undefined,
      gender: patient?.gender ?? undefined,
      bloodGroup: (patient?.bloodGroup as typeof BLOOD_GROUPS[number]) ?? undefined,
      occupation: patient?.occupation ?? '',
      primaryDoctorId: patient?.primaryDoctorId ?? '',
      medicalNotes: patient?.medicalNotes ?? '',
      contact: {
        primaryPhone: contact?.primaryPhone ?? '',
        alternatePhone: contact?.alternatePhone ?? '',
        email: contact?.email ?? '',
        addressLine1: contact?.addressLine1 ?? '',
        addressLine2: contact?.addressLine2 ?? '',
        city: contact?.city ?? '',
        state: contact?.state ?? '',
        nationality: contact?.nationality ?? 'IN',
        countryId: contact?.countryId ?? clinicDefaults?.countryId ?? '',
        stateId: contact?.stateId ?? clinicDefaults?.stateId ?? '',
        districtId: contact?.districtId ?? clinicDefaults?.districtId ?? '',
        pinCode: contact?.pinCode ?? '',
        emergencyContactName: contact?.emergencyContactName ?? '',
        emergencyContactRelation: contact?.emergencyContactRelation ?? '',
        emergencyContactPhone: contact?.emergencyContactPhone ?? '',
      },
    },
  });

  const [selectedCountryId, setSelectedCountryId] = useState('');
  const [selectedStateId, setSelectedStateId] = useState('');
  const indiaId = countries.find((c: { iso2: string }) => c.iso2 === 'IN')?.id ?? '';
  const effectiveCountryId = selectedCountryId || indiaId;

  const { data: states = [] } = useStates(effectiveCountryId || undefined);
  const { data: districts = [] } = useDistricts(selectedStateId || undefined);

  // Step 1: set country from clinic defaults (triggers useStates to load)
  useEffect(() => {
    if (mode !== 'create' || !clinicDefaults) return;
    const defaultCountry = clinicDefaults.countryId || indiaId;
    if (defaultCountry) {
      setSelectedCountryId(defaultCountry);
      form.setValue('contact.countryId', defaultCountry);
    }
  }, [clinicDefaults, indiaId, mode, form]);

  // Step 2: once states have loaded for the selected country, set state
  useEffect(() => {
    if (mode !== 'create' || !clinicDefaults || states.length === 0) return;
    if (clinicDefaults.stateId) {
      setSelectedStateId(clinicDefaults.stateId);
      form.setValue('contact.stateId', clinicDefaults.stateId);
    }
  }, [clinicDefaults, states, mode, form]);

  // Step 3: apply district only after district options are loaded (Radix Select needs matching items)
  useEffect(() => {
    if (mode !== 'create' || !clinicDefaults?.districtId || districts.length === 0) return;
    const match = districts.some((d) => d.id === clinicDefaults.districtId);
    if (match) {
      form.setValue('contact.districtId', clinicDefaults.districtId);
    }
  }, [clinicDefaults, districts, mode, form]);

  // Clear stale district when it is not in the current state's district list
  useEffect(() => {
    if (!selectedStateId || districts.length === 0) return;
    const currentDistrictId = form.getValues('contact.districtId');
    if (!currentDistrictId) return;
    if (!districts.some((d) => d.id === currentDistrictId)) {
      form.setValue('contact.districtId', '');
    }
  }, [selectedStateId, districts, form]);

  // Create mode: select first available doctor as primary when none chosen yet
  useEffect(() => {
    if (mode !== 'create') return;
    if (!doctors?.length) return;
    const current = form.getValues('primaryDoctorId');
    if (current) return;
    const firstDoctor = doctors[0];
    if (firstDoctor) form.setValue('primaryDoctorId', firstDoctor.id);
  }, [mode, doctors, form]);

  const [selectedConditions, setSelectedConditions] = useState<Set<string>>(new Set());
  const [selectedAllergies, setSelectedAllergies] = useState<Set<string>>(new Set());
  const [allergySeverity, setAllergySeverity] = useState<Record<string, string>>({});
  const [conditionsOpen, setConditionsOpen] = useState(false);
  const [allergiesOpen, setAllergiesOpen] = useState(false);

  useImperativeHandle(ref, () => ({
    submit: async () => {
      if (submittingRef.current) return;
      await form.handleSubmit(onSubmit)();
    },
    isBusy: () => submittingRef.current || busy,
  }));

  useEffect(() => {
    if (mode === 'edit') {
      form.reset({
        firstName: patient?.firstName ?? '',
        lastName: patient?.lastName ?? '',
        dateOfBirth: patient?.dateOfBirth instanceof Date && !isNaN(patient.dateOfBirth.getTime())
          ? patient.dateOfBirth.toISOString().split('T')[0]
          : undefined,
        age: patient?.age ?? undefined,
        gender: patient?.gender ?? undefined,
        bloodGroup: (patient?.bloodGroup as typeof BLOOD_GROUPS[number]) ?? undefined,
        occupation: patient?.occupation ?? '',
        primaryDoctorId: patient?.primaryDoctorId ?? '',
        medicalNotes: patient?.medicalNotes ?? '',
        contact: {
          primaryPhone: contact?.primaryPhone ?? '',
          alternatePhone: contact?.alternatePhone ?? '',
          email: contact?.email ?? '',
          addressLine1: contact?.addressLine1 ?? '',
          addressLine2: contact?.addressLine2 ?? '',
          city: contact?.city ?? '',
          state: contact?.state ?? '',
          nationality: contact?.nationality ?? 'IN',
          countryId: contact?.countryId ?? '',
          stateId: contact?.stateId ?? '',
          districtId: contact?.districtId ?? '',
          pinCode: contact?.pinCode ?? '',
          emergencyContactName: contact?.emergencyContactName ?? '',
          emergencyContactRelation: contact?.emergencyContactRelation ?? '',
          emergencyContactPhone: contact?.emergencyContactPhone ?? '',
        },
      });
      if (contact?.countryId) {
        setSelectedCountryId(contact.countryId);
      }
      if (contact?.stateId) {
        setSelectedStateId(contact.stateId);
      }
    }
  }, [mode, patient, contact, form]);

  const error = form.formState.errors.root?.message;

  function handleConditionsChange(ids: string[]) {
    setSelectedConditions(new Set(ids));
  }

  function handleAllergiesChange(ids: string[]) {
    setSelectedAllergies(new Set(ids));
    setAllergySeverity((sev) => {
      const updated = { ...sev };
      for (const key of Object.keys(updated)) {
        if (!ids.includes(key)) delete updated[key];
      }
      return updated;
    });
  }

  const conditionItems: MultiSelectItem[] = (orgConditions ?? []).map((c) => ({
    id: c.name,
    label: c.name,
  }));

  const allergyItems: MultiSelectItem[] = (orgAllergies ?? []).map((a) => ({
    id: a.name,
    label: a.name,
  }));

  async function onSubmit(values: PatientCreateInput | PatientUpdateInput) {
    if (submittingRef.current) return;
    setSubmitting(true);
    try {
      const contactPayload = buildContactPayload(values.contact as Record<string, string | undefined>, effectiveCountryId);

      if (mode === 'create') {
        const input = values as PatientCreateInput;
        const hasDob = !!input.dateOfBirth;
        const hasAge =
          input.age !== undefined && input.age !== null && !Number.isNaN(input.age);
        const savedPatient = await create.mutateAsync({
          input: {
            ...input,
            dateOfBirth: hasDob ? input.dateOfBirth : undefined,
            age: hasAge ? input.age : undefined,
            contact: contactPayload as PatientCreateInput['contact'],
          },
          key: idempotencyKey,
        });

        if (selectedConditions.size > 0) {
          await setConditions.mutateAsync({
            patientId: savedPatient.id,
            input: { conditions: Array.from(selectedConditions).map((name) => ({ name })) },
          });
        }
        if (selectedAllergies.size > 0) {
          await setAllergies.mutateAsync({
            patientId: savedPatient.id,
            input: {
              allergies: Array.from(selectedAllergies).map((name) => ({
                name,
                severity: allergySeverity[name] || undefined,
              })),
            },
          });
        }
        onSuccess(savedPatient.id);
      } else if (mode === 'edit' && patient) {
        const { bloodGroup, dateOfBirth, age, ...rest } = values as PatientCreateInput;
        const hasDob = !!dateOfBirth;
        const hasAge = age !== undefined && age !== null && !isNaN(age);

        const patchDateOfBirth = hasDob
          ? dateOfBirth
          : hasAge
            ? null
            : patient.dateOfBirth
              ? null
              : undefined;

        const patchAge = hasAge
          ? age
          : hasDob
            ? null
            : patient.age != null
              ? null
              : undefined;

        await update.mutateAsync({
          id: patient.id,
          input: {
            ...rest,
            dateOfBirth: patchDateOfBirth as string | null | undefined,
            age: patchAge as number | null | undefined,
            bloodGroup: (bloodGroup ?? undefined) as string | undefined,
            contact: contactPayload,
          } as UpdatePatientInput,
        });

        if (selectedConditions.size > 0) {
          await setConditions.mutateAsync({
            patientId: patient.id,
            input: { conditions: Array.from(selectedConditions).map((name) => ({ name })) },
          });
        }
        if (selectedAllergies.size > 0) {
          await setAllergies.mutateAsync({
            patientId: patient.id,
            input: {
              allergies: Array.from(selectedAllergies).map((name) => ({
                name,
                severity: allergySeverity[name] || undefined,
              })),
            },
          });
        }
        onSuccess(patient.id);
      }
    } catch (err) {
      if (err instanceof Error) markAsHandled(err);
      form.setError('root', {
        message: err instanceof Error ? err.message : 'Failed to save patient',
      });
    } finally {
      setSubmitting(false);
    }
  }

  const fieldAttrs = (name: string) =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- fieldAttrs bridges raw strings to RHF FieldPath
    form.register(name as any);

  const fieldError = (name: string): string | undefined => {
    const parts = name.split('.');
    let node: Record<string, unknown> | undefined = form.formState.errors;
    for (const p of parts) {
      if (!node) return undefined;
      node = node[p] as Record<string, unknown> | undefined;
    }
    return (node as { message?: string } | undefined)?.message;
  };

  const hasError = (name: string) => !!fieldError(name);

  const errInput = (name: string) =>
    hasError(name) ? 'border-coral-500 focus-visible:ring-coral-500' : '';

  const errLabel = (name: string) =>
    hasError(name) ? 'text-coral-600' : '';

  const renderError = (name: string) => {
    const msg = fieldError(name);
    return msg ? <p className="text-xs text-coral-600 mt-1 leading-tight">{msg}</p> : null;
  };

  return (
    <form onSubmit={form.handleSubmit(onSubmit)}>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* ── Section 1: Personal Information ─────────────────────────── */}
        <Card>
          <CardContent className="p-5 space-y-4">
            <div className="pb-2 border-b border-[var(--border-subtle)]">
              <div className="text-sm font-semibold text-ink-900">Personal Information</div>
              <div className="text-[11px] text-ink-500 mt-0.5">Patient demographics and basic details</div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="min-w-0">
                <Label htmlFor="firstName" className={errLabel('firstName')}>First Name *</Label>
                <Input id="firstName" {...fieldAttrs('firstName')} placeholder="Enter first name" className={errInput('firstName')} />
                {renderError('firstName')}
              </div>
              <div className="min-w-0">
                <Label htmlFor="lastName" className={errLabel('lastName')}>Last Name *</Label>
                <Input id="lastName" {...fieldAttrs('lastName')} placeholder="Enter last name" className={errInput('lastName')} />
                {renderError('lastName')}
              </div>
              <div className="min-w-0 sm:col-span-2">
                <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3">
                  <div className="min-w-0">
                    <Label htmlFor="dateOfBirth" className={errLabel('dateOfBirth')}>Date of Birth</Label>
                    <Controller
                      name="dateOfBirth"
                      control={form.control}
                      render={({ field }) => (
                        <DatePicker
                          id="dateOfBirth"
                          value={field.value ?? ''}
                          onChange={(val) => {
                            field.onChange(val || undefined);
                            if (val) form.setValue('age', calculateAge(val));
                          }}
                          placeholder="Pick a date"
                          error={hasError('dateOfBirth')}
                        />
                      )}
                    />
                  </div>
                  <div className="min-w-0 w-full sm:w-24">
                    <Label htmlFor="age" className={cn(errLabel('age'), hasError('dateOfBirth') && 'text-coral-600')}>Age (yrs)</Label>
                    <Controller
                      name="age"
                      control={form.control}
                      render={({ field }) => (
                        <Input
                          id="age"
                          type="number"
                          min={0}
                          max={150}
                          placeholder="—"
                          value={field.value ?? ''}
                          onChange={(e) => {
                            const raw = e.target.value;
                            const val = raw ? parseInt(raw, 10) : undefined;
                            field.onChange(val);
                            if (val !== undefined && !isNaN(val)) form.setValue('dateOfBirth', undefined);
                          }}
                          className={cn(errInput('age'), hasError('dateOfBirth') && 'border-coral-500 focus-visible:ring-coral-500')}
                        />
                      )}
                    />
                  </div>
                </div>
                <p className="text-[11px] text-ink-500 mt-1">Enter date of birth or age</p>
                {renderError('dateOfBirth')}
                {renderError('age')}
              </div>
              <div className="min-w-0">
                <Label htmlFor="gender" className={errLabel('gender')}>Gender *</Label>
                <Controller
                  name="gender"
                  control={form.control}
                  render={({ field }) => (
                    <Select value={field.value || undefined} onValueChange={field.onChange}>
                      <SelectTrigger id="gender" className={errInput('gender')}>
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {GENDER_OPTIONS.map((g) => (
                          <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                {renderError('gender')}
              </div>
              <div className="min-w-0">
                <Label htmlFor="bloodGroup" className={errLabel('bloodGroup')}>Blood Group</Label>
                <Controller
                  name="bloodGroup"
                  control={form.control}
                  render={({ field }) => (
                    <Select value={field.value || undefined} onValueChange={field.onChange}>
                      <SelectTrigger id="bloodGroup" className={errInput('bloodGroup')}>
                        <SelectValue placeholder="—" />
                      </SelectTrigger>
                      <SelectContent>
                        {BLOOD_GROUPS.map((bg) => (
                          <SelectItem key={bg} value={bg}>{bg}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                {renderError('bloodGroup')}
              </div>
              <div className="min-w-0">
                <Label htmlFor="occupation" className={errLabel('occupation')}>Occupation</Label>
                <Input id="occupation" {...fieldAttrs('occupation')} placeholder="e.g. Software Engineer" className={errInput('occupation')} />
                {renderError('occupation')}
              </div>
              <div className="min-w-0">
                <Label htmlFor="primaryDoctorId" className={errLabel('primaryDoctorId')}>Primary Doctor</Label>
                <Controller
                  name="primaryDoctorId"
                  control={form.control}
                  render={({ field }) => (
                    <Select value={field.value || undefined} onValueChange={field.onChange}>
                      <SelectTrigger id="primaryDoctorId" className={errInput('primaryDoctorId')}>
                        <SelectValue placeholder="Not assigned" />
                      </SelectTrigger>
                      <SelectContent>
                        {(doctors ?? []).map((d) => (
                          <SelectItem key={d.id} value={d.id}>{d.displayName}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                {renderError('primaryDoctorId')}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ── Section 2: Contact Information ──────────────────────────── */}
        <Card>
          <CardContent className="p-5 space-y-4">
            <div className="pb-2 border-b border-[var(--border-subtle)]">
              <div className="text-sm font-semibold text-ink-900">Contact Information</div>
              <div className="text-[11px] text-ink-500 mt-0.5">Phone, email and address details</div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="min-w-0">
                <FormPhoneField
                  name="contact.primaryPhone"
                  control={form.control}
                  id="primaryPhone"
                  label="Phone"
                  placeholder="Enter phone number"
                  required
                />
              </div>
              <div className="min-w-0">
                <FormPhoneField
                  name="contact.alternatePhone"
                  control={form.control}
                  id="alternatePhone"
                  label="Alternate Phone"
                  placeholder="Alternate phone"
                />
              </div>
              <div className="min-w-0 sm:col-span-2">
                <Label htmlFor="email" className={errLabel('contact.email')}>Email</Label>
                <Input id="email" type="email" {...fieldAttrs('contact.email')} placeholder="patient@example.com" className={errInput('contact.email')} />
                {renderError('contact.email')}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ── Section 3: Address ──────────────────────────────────────── */}
        <Card>
          <CardContent className="p-5 space-y-4">
            <div className="pb-2 border-b border-[var(--border-subtle)]">
              <div className="text-sm font-semibold text-ink-900">Address</div>
              <div className="text-[11px] text-ink-500 mt-0.5">Residential address details</div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="min-w-0">
                <Label htmlFor="addressLine1" className={errLabel('contact.addressLine1')}>Address Line 1</Label>
                <Input id="addressLine1" {...fieldAttrs('contact.addressLine1')} placeholder="Street, building, house no." className={errInput('contact.addressLine1')} />
                {renderError('contact.addressLine1')}
              </div>
              <div className="min-w-0">
                <Label htmlFor="addressLine2" className={errLabel('contact.addressLine2')}>Address Line 2</Label>
                <Input id="addressLine2" {...fieldAttrs('contact.addressLine2')} placeholder="Area, landmark" className={errInput('contact.addressLine2')} />
                {renderError('contact.addressLine2')}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="min-w-0">
                <Label className={errLabel('contact.countryId')}>Country</Label>
                <Controller
                  name="contact.countryId"
                  control={form.control}
                  render={({ field }) => (
                    <Select
                      value={field.value || undefined}
                      onValueChange={(v) => {
                        field.onChange(v);
                        setSelectedCountryId(v);
                        setSelectedStateId('');
                        form.setValue('contact.stateId', '');
                        form.setValue('contact.districtId', '');
                      }}
                    >
                      <SelectTrigger id="contact.countryId" className={errInput('contact.countryId')}>
                        <SelectValue placeholder="Select country" />
                      </SelectTrigger>
                      <SelectContent>
                        {countries.map((c) => (
                          <SelectItem key={c.iso2} value={c.id}>
                            <span className="mr-1">{c.flagEmoji}</span>
                            {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                {renderError('contact.countryId')}
              </div>
              <div className="min-w-0">
                <Label>State</Label>
                <Controller
                  name="contact.stateId"
                  control={form.control}
                  render={({ field }) => (
                    <Select
                      value={field.value || undefined}
                      onValueChange={(v) => {
                        field.onChange(v);
                        setSelectedStateId(v);
                        form.setValue('contact.districtId', '');
                      }}
                    >
                      <SelectTrigger id="contact.stateId">
                        <SelectValue placeholder="Select state" />
                      </SelectTrigger>
                      <SelectContent>
                        {states.map((s) => (
                          <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                {renderError('contact.stateId')}
              </div>
              <div className="min-w-0">
                <Label className={errLabel('contact.districtId')}>District</Label>
                <Controller
                  name="contact.districtId"
                  control={form.control}
                  render={({ field }) => (
                    <Select value={field.value || undefined} onValueChange={field.onChange}>
                      <SelectTrigger id="contact.districtId" className={errInput('contact.districtId')}>
                        <SelectValue placeholder="Select district" />
                      </SelectTrigger>
                      <SelectContent>
                        {districts.map((d) => (
                          <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                {renderError('contact.districtId')}
              </div>
              <div className="min-w-0">
                <Label htmlFor="pinCode" className={errLabel('contact.pinCode')}>Pin Code</Label>
                <Input id="pinCode" {...fieldAttrs('contact.pinCode')} placeholder="e.g. 560001" className={errInput('contact.pinCode')} />
                {renderError('contact.pinCode')}
              </div>
              <div className="min-w-0">
                <Label htmlFor="city" className={errLabel('contact.city')}>City</Label>
                <Input id="city" {...fieldAttrs('contact.city')} placeholder="City" className={errInput('contact.city')} />
                {renderError('contact.city')}
              </div>
              <div className="min-w-0">
                <Label htmlFor="nationality" className={errLabel('contact.nationality')}>Nationality</Label>
                <Controller
                  name="contact.nationality"
                  control={form.control}
                  defaultValue="IN"
                  render={({ field }) => (
                    <CountrySelect
                      countries={countries.map((c) => ({
                        id: c.id,
                        iso2: c.iso2,
                        name: c.name,
                        flagEmoji: c.flagEmoji,
                        phoneCode: c.phoneCode,
                      }))}
                      value={field.value ?? 'IN'}
                      onChange={field.onChange}
                      placeholder="Select country"
                      hidePhoneCode
                    />
                  )}
                />
                {renderError('contact.nationality')}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ── Section 4: Emergency Contact ─────────────────────────────── */}
        <Card>
          <CardContent className="p-5 space-y-4">
            <div className="pb-2 border-b border-[var(--border-subtle)]">
              <div className="text-sm font-semibold text-ink-900">Emergency Contact</div>
              <div className="text-[11px] text-ink-500 mt-0.5">Who to reach in case of an emergency</div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="min-w-0">
                <Label htmlFor="emergencyContactName" className={errLabel('contact.emergencyContactName')}>Contact Name</Label>
                <Input id="emergencyContactName" {...fieldAttrs('contact.emergencyContactName')} placeholder="Full name" className={errInput('contact.emergencyContactName')} />
                {renderError('contact.emergencyContactName')}
              </div>
              <div className="min-w-0">
                <Label htmlFor="emergencyContactRelation" className={errLabel('contact.emergencyContactRelation')}>Relationship</Label>
                <Controller
                  name="contact.emergencyContactRelation"
                  control={form.control}
                  render={({ field }) => (
                    <Select value={field.value || undefined} onValueChange={field.onChange}>
                      <SelectTrigger id="emergencyContactRelation" className={errInput('contact.emergencyContactRelation')}>
                        <SelectValue placeholder="Select relationship" />
                      </SelectTrigger>
                      <SelectContent>
                        {RELATIONSHIP_OPTIONS.map((r) => (
                          <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                {renderError('contact.emergencyContactRelation')}
              </div>
              <div className="min-w-0 sm:col-span-2">
                <FormPhoneField
                  name="contact.emergencyContactPhone"
                  control={form.control}
                  id="emergencyPhone"
                  label="Emergency Phone"
                  placeholder="Emergency phone"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ── Section 7: Medical Notes ── row-span-2 ───────────────────── */}
        <Card className="lg:row-span-2">
          <CardContent className="p-5 space-y-4 h-full flex flex-col">
            <div className="pb-2 border-b border-[var(--border-subtle)]">
              <div className="text-sm font-semibold text-ink-900">Medical Notes</div>
              <div className="text-[11px] text-ink-500 mt-0.5">Initial clinical observations</div>
            </div>
            <div className="flex-1 flex flex-col">
              <Label htmlFor="medicalNotes">Notes</Label>
              <Textarea
                id="medicalNotes"
                {...fieldAttrs('medicalNotes')}
                rows={6}
                placeholder="Any relevant medical notes for this patient…"
                className={cn('flex-1 min-h-[120px]', errInput('medicalNotes'))}
              />
              {renderError('medicalNotes')}
            </div>
          </CardContent>
        </Card>

        {/* ── Section 5: Medical Conditions ─────────────────────────────── */}
        <Card>
          <CardContent className="p-5 space-y-4">
            <div className="pb-2 border-b border-[var(--border-subtle)]">
              <div className="text-sm font-semibold text-ink-900">Systemic Medical Conditions</div>
              <div className="text-[11px] text-ink-500 mt-0.5">Select conditions from the popup</div>
            </div>
            {(orgConditions?.length ?? 0) > 0 ? (
              <>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full justify-start h-auto py-2.5"
                  onClick={() => setConditionsOpen(true)}
                >
                  <svg className="size-4 mr-2 flex-none" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                  Select Conditions{selectedConditions.size > 0 ? ` (${selectedConditions.size} selected)` : ''}
                </Button>
                {selectedConditions.size > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {Array.from(selectedConditions).map((name, idx) => (
                      <MultiSelectBadge
                        key={name}
                        label={name}
                        colorIndex={idx}
                        onRemove={() => {
                          setSelectedConditions((prev) => {
                            const next = new Set(prev);
                            next.delete(name);
                            return next;
                          });
                        }}
                      />
                    ))}
                  </div>
                )}
                <MultiSelectPopup
                  open={conditionsOpen}
                  onOpenChange={setConditionsOpen}
                  title="Systemic Medical Conditions"
                  description="Select all conditions that apply to this patient"
                  items={conditionItems}
                  selected={Array.from(selectedConditions)}
                  onSelectionChange={handleConditionsChange}
                  searchPlaceholder="Search conditions…"
                />
              </>
            ) : (
              <div className="text-sm text-ink-500 text-center py-4 border-2 border-dashed border-surface-200 rounded-xl">
                No medical conditions configured. An admin can add them in Settings.
              </div>
            )}
          </CardContent>
        </Card>

        {/* ── Section 6: Allergies ───────────────────────────────────────── */}
        <Card>
          <CardContent className="p-5 space-y-4">
            <div className="pb-2 border-b border-[var(--border-subtle)]">
              <div className="text-sm font-semibold text-ink-900">Allergies &amp; Adverse Reactions</div>
              <div className="text-[11px] text-ink-500 mt-0.5">Select allergies from the popup</div>
            </div>
            {(orgAllergies?.length ?? 0) > 0 ? (
              <>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full justify-start h-auto py-2.5"
                  onClick={() => setAllergiesOpen(true)}
                >
                  <svg className="size-4 mr-2 flex-none" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                  Select Allergies{selectedAllergies.size > 0 ? ` (${selectedAllergies.size} selected)` : ''}
                </Button>
                {selectedAllergies.size > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {Array.from(selectedAllergies).map((name, idx) => (
                      <MultiSelectBadge
                        key={name}
                        label={name}
                        colorIndex={idx}
                        onRemove={() => {
                          setSelectedAllergies((prev) => {
                            const next = new Set(prev);
                            next.delete(name);
                            return next;
                          });
                        }}
                      >
                        <span className="ml-1 mr-0.5 text-[11px] text-ink-500">—</span>
                        <NativeSelect
                          value={allergySeverity[name] || ''}
                          onChange={(e) => setAllergySeverity((prev) => ({ ...prev, [name]: e.target.value }))}
                          className="h-auto text-[11px] bg-transparent border-none shadow-none focus-visible:ring-0 cursor-pointer text-ink-600 font-medium py-0 px-0 w-auto min-w-0 rounded-none"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <option value="">Severity</option>
                          <option value="mild">Mild</option>
                          <option value="moderate">Moderate</option>
                          <option value="severe">Severe</option>
                        </NativeSelect>
                      </MultiSelectBadge>
                    ))}
                  </div>
                )}
                <MultiSelectPopup
                  open={allergiesOpen}
                  onOpenChange={setAllergiesOpen}
                  title="Allergies & Adverse Reactions"
                  description="Select all allergies and adverse reactions for this patient"
                  items={allergyItems}
                  selected={Array.from(selectedAllergies)}
                  onSelectionChange={handleAllergiesChange}
                  searchPlaceholder="Search allergies…"
                />
              </>
            ) : (
              <div className="text-sm text-ink-500 text-center py-4 border-2 border-dashed border-surface-200 rounded-xl">
                No allergies configured. An admin can add them in Settings.
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {error && (
        <div className="mt-4 bg-coral-50 border border-coral-100 rounded-xl p-4 text-sm text-coral-700">
          <div className="flex items-start gap-2">
            <svg className="w-4 h-4 mt-0.5 flex-none" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>
            <span><span className="font-semibold">Error:</span> {error}</span>
          </div>
        </div>
      )}

      {mode === 'edit' && onCancel && (
        <div className="mt-4 flex items-center gap-2">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      )}

    </form>
  );
});
